import type {
  AuditEvent,
  DashboardResponse,
  EvidenceView,
  HealthStatus,
  PolicyView,
  ProblemDetail,
  TaskDetail,
  TaskSummary,
} from "./types";

const API_ROOT = "/api/v1";
const DEMO_TOKEN_KEY = "assureops-demo-access-token";

function bootstrapDemoToken(): string | null {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = fragment.get("access_token");
  if (token) {
    window.sessionStorage.setItem(DEMO_TOKEN_KEY, token);
    fragment.delete("access_token");
    const suffix = fragment.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${suffix ? `#${suffix}` : ""}`);
    return token;
  }
  return window.sessionStorage.getItem(DEMO_TOKEN_KEY);
}

const demoAccessToken = bootstrapDemoToken();

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_ROOT}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(demoAccessToken ? { Authorization: `Bearer ${demoAccessToken}` } : {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const problem = await response.json().catch(() => ({} as ProblemDetail));
    throw new ApiError(
      problem.detail || problem.title || `Request failed (${response.status})`,
      response.status,
      problem.code,
    );
  }
  return response.json() as Promise<T>;
}

export const api = {
  health: () => request<HealthStatus>("/health"),
  dashboard: () => request<DashboardResponse>("/dashboard"),
  task: (taskId: string) => request<TaskDetail>(`/tasks/${encodeURIComponent(taskId)}`),
  reset: () => request<{ task_id: string; location?: string }>("/demo/reset", { method: "POST" }),
  review: (
    task: TaskDetail,
    verdict: "approve" | "request_changes",
    reason: string,
  ) => request<TaskDetail>(`/tasks/${encodeURIComponent(task.id)}/reviews`, {
    method: "POST",
    headers: demoAccessToken ? {} : { "X-AssureOps-Local-Principal": "user:demo-reviewer" },
    body: JSON.stringify({
      verdict,
      reason,
      expected_revision: task.revision,
      idempotency_key: crypto.randomUUID(),
    }),
  }),
  execute: (task: TaskDetail) => request<TaskDetail>(
    `/tasks/${encodeURIComponent(task.id)}/executions`,
    {
      method: "POST",
      body: JSON.stringify({
        expected_revision: task.revision,
        idempotency_key: crypto.randomUUID(),
      }),
    },
  ),
  evidence: async (taskId: string) => {
    const result = await request<{ items: EvidenceView[] } | EvidenceView[]>(
      `/tasks/${encodeURIComponent(taskId)}/evidence`,
    );
    return Array.isArray(result) ? result : result.items;
  },
  audit: async (taskId: string) => {
    const result = await request<{ items: AuditEvent[] } | AuditEvent[]>(
      `/tasks/${encodeURIComponent(taskId)}/audit`,
    );
    return Array.isArray(result) ? result : result.items;
  },
  policies: async () => {
    const result = await request<{ items: PolicyView[] } | PolicyView[]>("/policies");
    return Array.isArray(result) ? result : result.items;
  },
};
